/**
 * After a deploy, a tab still running the previous build can ask for code files that are gone.
 * Browsers and bundlers word that failure differently; these are the ones Next.js (Turbopack),
 * Vite and webpack surface. Shared by the storefront and the admin.
 */
const STALE_BUILD_PATTERNS = [
  // Turbopack, which builds the shop: "Failed to load chunk /_next/static/chunks/… from module …".
  /Failed to load chunk/i,
  // Turbopack again, when the new build's module ids no longer match the old tab's: "Module 85255
  // was instantiated because it was required from module …, but the module factory is not available".
  /module factory is not available/i,
  /ChunkLoadError/i,
  /Loading (CSS )?chunk [\w-]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
];

export function isStaleBuildError(error: { name?: string; message?: string }): boolean {
  const text = `${error.name ?? ""} ${error.message ?? ""}`;
  return STALE_BUILD_PATTERNS.some((pattern) => pattern.test(text));
}

const RELOADED_KEY = "milqit:reloaded-for-new-build";

/**
 * Reloads once to pick up the new build. Returns false (and doesn't reload) if this tab already
 * tried in the last minute, so a real outage can't loop the page.
 */
export function reloadOnceForNewBuild(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOADED_KEY) ?? 0);
    if (Date.now() - last < 60_000) return false;
    sessionStorage.setItem(RELOADED_KEY, String(Date.now()));
  } catch {
    // Storage blocked (private mode): reloading once is still safe; the guard just can't persist.
  }
  window.location.reload();
  return true;
}

/**
 * For the page's <head>, before any code file loads: a cached page can name files a deploy has
 * deleted, and then nothing runs (not even the error page), so only inline code can recover.
 * Shares `reloadOnceForNewBuild`'s guard.
 */
export const RELOAD_ON_MISSING_CODE_SCRIPT = `addEventListener("error",function(e){var t=e.target;if(!t||t.tagName!=="SCRIPT"||(t.src||"").indexOf("/_next/static/")<0)return;try{var k=${JSON.stringify(RELOADED_KEY)},l=Number(sessionStorage.getItem(k)||0);if(Date.now()-l<6e4)return;sessionStorage.setItem(k,String(Date.now()))}catch(x){}location.reload()},true)`;
